import {
  cloneElement,
  isValidElement,
  type ComponentType,
  type ReactElement,
  type ReactNode,
  type CSSProperties,
} from 'react';
import type { LucideProps } from '@vitness/icons';
import { MobileNav } from './MobileNav.js';
import { ShellBackground } from './ShellBackground.js';
import { TopBarSearch } from './TopBarSearch.js';
import type { CommandItem } from './CommandPalette.js';
import {
  SidebarProvider,
  SidebarShell,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarCollapsible,
  SidebarTrigger,
  SidebarRail,
  useSidebar,
} from './AppSidebar.js';

export interface PageNavItem {
  id: string;
  label: string;
  icon: ComponentType<LucideProps>;
  children?: PageNavItem[];
  href?: string;
  onClick?: () => void;
  mobileNav?: boolean;
  /**
   * Render `children` as a non-collapsible labelled section (uppercase group
   * label + flat items), rather than the default collapsible group.
   */
  section?: boolean;
  /** Trailing status dot on a leaf item. */
  status?: 'operational' | 'degraded' | 'down';
  /** Trailing badge text on a leaf item (e.g. `BETA`). */
  badge?: string;
}

/** Configuration for the universal top-bar search (see {@link PageLayoutProps.search}). */
export interface TopBarSearchConfig {
  placeholder?: string;
  commands?: CommandItem[];
  /** Bind ⌘K / Ctrl+K to open the palette. Defaults to true. */
  shortcut?: boolean;
}

export interface PageLayoutProps {
  navItems: PageNavItem[];
  activeId?: string;
  onNavigate?: (id: string) => void;
  header?: ReactNode;
  collapsedHeader?: ReactNode;
  /**
   * Optional content rendered directly under the brand header and above the
   * navigation — the home for an organization / product switcher. Hidden when
   * the desktop rail is collapsed to icon mode.
   */
  secondary?: ReactNode;
  sidebarFooter?: ReactNode;
  /**
   * Content rendered inside the navigation drawer on mobile only, under the
   * secondary slot. Home for controls that live in the desktop top bar but have
   * no room there on mobile — e.g. an environment switcher.
   */
  mobileDrawerExtra?: ReactNode;
  mobileTitle?: ReactNode;
  mobileActions?: ReactNode;
  children: ReactNode;
  maxWidth?: number;
  padding?: string;
  mobileMaxItems?: number;
  contentStyle?: CSSProperties;
  mobilePinnedIds?: string[];
  onMobilePinnedChange?: (ids: string[]) => void;
  mobileEditable?: boolean;
  mobileLocked?: boolean;
  /**
   * Desktop top bar. Provide `topBar` to render a fully custom bar, or compose
   * one from `topBarTitle` / `topBarStart` / `topBarActions`. The sidebar
   * collapse trigger is prepended to the composed bar automatically.
   */
  topBar?: ReactNode;
  /** Page title shown at the start of the composed top bar. */
  topBarTitle?: ReactNode;
  /**
   * App-contextual control rendered after the universal search on desktop
   * (e.g. a Production/Sandbox environment switcher). Hidden on the mobile top
   * bar to preserve space — surface it via the drawer or `mobileActions`.
   */
  topBarStart?: ReactNode;
  /** Content pinned to the end of the top bar (notifications, docs, …). */
  topBarActions?: ReactNode;
  /**
   * Optional full-width bar rendered directly beneath the top bar (a second
   * row), e.g. breadcrumbs that don't fit the bar on mobile. Undefined by
   * default, so other apps are unaffected.
   */
  subBar?: ReactNode;
  /**
   * The universal top-bar search. Pass `true` for defaults, or an object to
   * customise the placeholder / command corpus / shortcut. Rendered as the wide
   * command-palette field on desktop and a compact icon button on mobile so it
   * is always present, regardless of app type.
   */
  search?: boolean | TopBarSearchConfig;
  /**
   * Hide the search field on desktop while keeping the compact icon on mobile.
   * Useful when the sidebar already exposes every destination and the bar is
   * better spent on context. The ⌘K palette still works. Defaults to false.
   */
  hideSearchOnDesktop?: boolean;
  /** Content background treatment. Defaults to `dots`. */
  background?: 'dots' | 'none';
}

const TOP_BAR_HEIGHT = 56;

/**
 * Hover rules for the top bar's inline-styled controls. Defined once here (the
 * bar is always mounted) so the platform host — which ships no Tailwind — gets
 * working :hover states for ghost buttons, bordered controls, and menu rows.
 */
const TOP_BAR_STYLES = `
  .vn-tb-ghost:hover{background:var(--vn-surface-card-hover);color:var(--vn-text-primary)}
  .vn-tb-ghost:hover svg{stroke:var(--vn-text-primary)}
  .vn-tb-soft:hover{border-color:var(--vn-border-strong);background:var(--vn-surface-card-hover)}
  .vn-tb-item:hover{background:var(--vn-surface-card-hover)}
`;

/**
 * Canonical content padding, mirrored from the system-design reference
 * (`Platform.dc.html` main content container). Fluidly scales the gutter
 * with the viewport instead of using a fixed, looser pad. Single source of
 * truth so every app's shell content lines up with the design system.
 */
export const CONTENT_PADDING = 'clamp(18px, 2.6vw, 28px)';

/**
 * Canonical max content width for full-width product screens, mirrored from
 * the system-design reference. Docs-style surfaces may intentionally use a
 * narrower measure for readability.
 */
export const CONTENT_MAX_WIDTH = 1340;

function flattenLeaves(items: PageNavItem[]): PageNavItem[] {
  const result: PageNavItem[] = [];
  for (const item of items) {
    if (item.children?.length) {
      result.push(...flattenLeaves(item.children));
    } else {
      result.push(item);
    }
  }
  return result;
}

function hasActiveDescendant(item: PageNavItem, activeId?: string): boolean {
  if (item.id === activeId) return true;
  return item.children?.some((child) => hasActiveDescendant(child, activeId)) ?? false;
}

/** Sticky desktop top bar — a translucent, blurred 56px header above the content. */
function TopBar({
  custom,
  title,
  start,
  actions,
}: {
  custom?: ReactNode;
  title?: ReactNode;
  start?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        height: TOP_BAR_HEIGHT,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '0 20px',
        borderBottom: '1px solid var(--vn-border-default)',
        background: 'var(--vn-nav-bg)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <style>{TOP_BAR_STYLES}</style>
      {custom ?? (
        <>
          {start}
          {title != null && (
            <div
              style={{
                fontFamily: 'var(--vn-font-display)',
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: 'var(--vn-text-primary)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {title}
            </div>
          )}
          {actions != null && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              {actions}
            </div>
          )}
        </>
      )}
    </header>
  );
}

/** Brand header that swaps the lockup for the chip mark in collapsed icon mode. */
function BrandHeader({
  header,
  collapsedHeader,
}: {
  header?: ReactNode;
  collapsedHeader?: ReactNode;
}) {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  return (
    <SidebarHeader
      style={{ height: 56, minHeight: 56, borderBottom: '1px solid var(--vn-border-default)' }}
    >
      {collapsed ? collapsedHeader ?? header : header}
    </SidebarHeader>
  );
}

/**
 * Secondary slot (e.g. an org/product switcher). Stays mounted in collapsed
 * icon mode and injects a `collapsed` prop into its child so the switcher can
 * reduce to an avatar trigger rather than disappearing.
 */
function SecondarySlot({ children }: { children: ReactNode }) {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const content = isValidElement(children)
    ? cloneElement(children as ReactElement<{ collapsed?: boolean }>, { collapsed })
    : children;
  return (
    <div
      style={{
        padding: collapsed ? '8px 0' : '10px 12px 6px',
        borderBottom: '1px solid var(--vn-border-default)',
      }}
    >
      {content}
    </div>
  );
}

/**
 * Footer slot. Stays mounted in collapsed icon mode and injects a `collapsed`
 * prop into the footer element so a {@link SidebarAccount} (or similar) can
 * reduce to its avatar/identity — keeping the Clerk user button reachable on
 * the icon rail rather than disappearing.
 */
function FooterSlot({ children }: { children: ReactNode }) {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const content =
    isValidElement(children)
      ? cloneElement(children as ReactElement<{ collapsed?: boolean }>, { collapsed })
      : children;
  return <SidebarFooter style={collapsed ? { padding: 8, alignItems: 'center' } : undefined}>{content}</SidebarFooter>;
}

function navHandler(
  item: PageNavItem,
  onNavigate?: (id: string) => void,
  onAfter?: () => void,
) {
  return () => {
    onNavigate?.(item.id);
    item.onClick?.();
    onAfter?.();
  };
}

/** Renders nested nav items into the sub-list (leaves + further collapsibles). */
function renderSubItems(
  items: PageNavItem[],
  activeId: string | undefined,
  onNavigate: ((id: string) => void) | undefined,
  onAfter?: () => void,
): ReactNode {
  return items.map((child) =>
    child.children?.length ? (
      <SidebarCollapsible
        key={child.id}
        icon={child.icon}
        label={child.label}
        isActive={hasActiveDescendant(child, activeId)}
        defaultOpen
      >
        <SidebarMenuSub>{renderSubItems(child.children, activeId, onNavigate, onAfter)}</SidebarMenuSub>
      </SidebarCollapsible>
    ) : (
      <SidebarMenuSubItem key={child.id}>
        <SidebarMenuSubButton isActive={activeId === child.id} onClick={navHandler(child, onNavigate, onAfter)}>
          {child.label}
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    ),
  );
}

/** A single leaf nav row (button with optional trailing status dot / badge). */
function renderLeaf(
  item: PageNavItem,
  activeId: string | undefined,
  onNavigate: ((id: string) => void) | undefined,
  onAfter?: () => void,
): ReactNode {
  return (
    <SidebarMenuItem key={item.id}>
      <SidebarMenuButton
        icon={item.icon}
        isActive={activeId === item.id}
        status={item.status}
        badge={item.badge}
        onClick={navHandler(item, onNavigate, onAfter)}
      >
        {item.label}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/** A non-section top-level item: a collapsible group (has children) or a leaf. */
function renderTopItem(
  item: PageNavItem,
  activeId: string | undefined,
  onNavigate: ((id: string) => void) | undefined,
  onAfter?: () => void,
): ReactNode {
  if (item.children?.length) {
    return (
      <SidebarCollapsible
        key={item.id}
        icon={item.icon}
        label={item.label}
        isActive={hasActiveDescendant(item, activeId)}
        defaultOpen
      >
        <SidebarMenuSub>{renderSubItems(item.children, activeId, onNavigate, onAfter)}</SidebarMenuSub>
      </SidebarCollapsible>
    );
  }
  return renderLeaf(item, activeId, onNavigate, onAfter);
}

/**
 * Renders the nav tree. Items flagged `section` become non-collapsible labelled
 * groups (uppercase label); section children are leaves, or collapsible groups
 * when they carry `children` of their own. Everything else batches into menus.
 */
function renderNav(
  items: PageNavItem[],
  activeId: string | undefined,
  onNavigate: ((id: string) => void) | undefined,
  onAfter?: () => void,
): ReactNode {
  const out: ReactNode[] = [];
  let buffer: PageNavItem[] = [];
  let menuKey = 0;

  const flush = () => {
    if (buffer.length === 0) return;
    const batch = buffer;
    buffer = [];
    out.push(
      <SidebarMenu key={`menu-${menuKey++}`}>
        {batch.map((item) => renderTopItem(item, activeId, onNavigate, onAfter))}
      </SidebarMenu>,
    );
  };

  for (const item of items) {
    if (item.section && item.children?.length) {
      flush();
      out.push(
        <SidebarGroup key={item.id} label={item.label}>
          <SidebarGroupLabel>{item.label}</SidebarGroupLabel>
          <SidebarMenu>
            {item.children.map((child) => renderTopItem(child, activeId, onNavigate, onAfter))}
          </SidebarMenu>
        </SidebarGroup>,
      );
    } else {
      buffer.push(item);
    }
  }
  flush();

  return out;
}

/**
 * The responsive shell body, in three shapes rather than two:
 *
 * - **≥1024** — the full expanded rail, collapsible to icons by preference.
 * - **768–1023** — the same inline rail, opening in icon mode. No bottom nav:
 *   there is a rail on screen, and a second navigation surface stacked under
 *   the content would be duplicating it at the cost of 56px of a short viewport.
 * - **<768** — off-canvas drawer plus the bottom nav.
 *
 * The middle case is the one this used to get wrong. `isMobile` was derived as
 * `!isDesktop`, so every tablet and half-screen window got the phone shell —
 * 10px bottom-nav labels stretched across a 1000px screen — while the content
 * inside it had already switched back to its desktop layout at 560/720. See
 * `useBreakpoint`.
 */
function ShellBody({
  navItems,
  activeId,
  onNavigate,
  header,
  collapsedHeader,
  secondary,
  sidebarFooter,
  mobileDrawerExtra,
  mobileTitle,
  mobileActions,
  children,
  maxWidth,
  padding,
  contentStyle,
  showBackground,
  mobileItems,
  mobileMaxItems,
  mobilePinnedIds,
  onMobilePinnedChange,
  mobileEditable,
  mobileLocked,
  topBar,
  topBarTitle,
  topBarStart,
  topBarActions,
  subBar,
  search,
  hideSearchOnDesktop,
}: {
  navItems: PageNavItem[];
  activeId?: string;
  onNavigate?: (id: string) => void;
  header?: ReactNode;
  collapsedHeader?: ReactNode;
  secondary?: ReactNode;
  sidebarFooter?: ReactNode;
  mobileDrawerExtra?: ReactNode;
  mobileTitle?: ReactNode;
  mobileActions?: ReactNode;
  children: ReactNode;
  maxWidth?: number;
  padding: string;
  contentStyle?: CSSProperties;
  showBackground: boolean;
  mobileItems: PageNavItem[];
  mobileMaxItems: number;
  mobilePinnedIds?: string[];
  onMobilePinnedChange?: (ids: string[]) => void;
  mobileEditable: boolean;
  mobileLocked: boolean;
  topBar?: ReactNode;
  topBarTitle?: ReactNode;
  topBarStart?: ReactNode;
  topBarActions?: ReactNode;
  subBar?: ReactNode;
  search?: boolean | TopBarSearchConfig;
  hideSearchOnDesktop?: boolean;
}) {
  const { isMobile, setOpenMobile } = useSidebar();
  const closeMobile = isMobile ? () => setOpenMobile(false) : undefined;

  // Universal search: the wide field on desktop, a compact icon on mobile.
  const searchConfig: TopBarSearchConfig | undefined = search
    ? typeof search === 'object'
      ? search
      : {}
    : undefined;
  const searchEl = searchConfig ? (
    <TopBarSearch
      variant={isMobile ? 'icon' : 'box'}
      placeholder={searchConfig.placeholder}
      commands={searchConfig.commands}
      shortcut={searchConfig.shortcut}
    />
  ) : null;

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100dvh / var(--vd-zoom, 1))' }}>
      <SidebarShell aria-label="Primary navigation">
        <BrandHeader header={header} collapsedHeader={collapsedHeader} />
        {secondary != null && <SecondarySlot>{secondary}</SecondarySlot>}
        {isMobile && mobileDrawerExtra != null && (
          <div
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid var(--vn-border-default)',
            }}
          >
            {mobileDrawerExtra}
          </div>
        )}
        <SidebarContent style={{ padding: '8px' }}>
          {renderNav(navItems, activeId, onNavigate, closeMobile)}
        </SidebarContent>
        {sidebarFooter != null && <FooterSlot>{sidebarFooter}</FooterSlot>}
        <SidebarRail />
      </SidebarShell>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* One top bar for both viewports: its SidebarTrigger collapses the rail
            on desktop and opens the drawer on mobile — same icon either way. The
            universal search is the wide field after the trigger on desktop, and
            a compact icon folded into the actions cluster on mobile so it stays
            present without crowding the bar. App-contextual `topBarStart` (e.g.
            the env switcher) is desktop-only. */}
        <TopBar
          custom={topBar}
          title={isMobile ? mobileTitle ?? topBarTitle : topBarTitle}
          start={
            <>
              <SidebarTrigger />
              {!isMobile && !hideSearchOnDesktop && searchEl}
              {!isMobile && topBarStart}
            </>
          }
          actions={
            isMobile ? (
              <>
                {searchEl}
                {mobileActions ?? topBarActions}
              </>
            ) : (
              topBarActions
            )
          }
        />

        {subBar != null && (
          <div
            style={{
              position: 'sticky',
              top: TOP_BAR_HEIGHT,
              zIndex: 29,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              minHeight: 40,
              padding: '0 16px',
              borderBottom: '1px solid var(--vn-border-default)',
              background: 'var(--vn-nav-bg)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
          >
            {subBar}
          </div>
        )}

        <main
          // `overflow-x: hidden` computes `overflow-y` to `auto`, which makes
          // this element — not `body` — the page's scroll container. Marked so
          // modal surfaces can freeze the right thing without knowing that;
          // locking `body` (which is pinned to `height: 100%` and never
          // scrolls) is a no-op that reads as a working scroll lock.
          data-scroll-container=""
          style={{
            flex: 1,
            position: 'relative',
            overflowX: 'hidden',
            // 72 clears the 56px nav on a flat-bottomed phone; on a notched one
            // the nav itself grows by the safe-area inset, so the content
            // clearance has to grow with it or the last row hides behind it.
            paddingBottom: isMobile ? 'calc(72px + var(--vn-safe-bottom, 0px))' : undefined,
            transition: 'background 0.2s ease, color 0.15s ease',
          }}
        >
          {showBackground && <ShellBackground />}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              padding: isMobile ? CONTENT_PADDING : padding,
              maxWidth,
              ...contentStyle,
            }}
          >
            {children}
          </div>
        </main>

        {isMobile && (
          <MobileNav
            items={mobileItems}
            activeId={activeId}
            onNavigate={onNavigate}
            maxItems={mobileMaxItems}
            pinnedIds={mobilePinnedIds}
            onPinnedChange={onMobilePinnedChange}
            editable={mobileEditable}
            locked={mobileLocked}
          />
        )}
      </div>
    </div>
  );
}

export function PageLayout({
  navItems,
  activeId,
  onNavigate,
  header,
  collapsedHeader,
  secondary,
  sidebarFooter,
  mobileDrawerExtra,
  mobileTitle,
  mobileActions,
  children,
  maxWidth,
  padding = CONTENT_PADDING,
  mobileMaxItems = 5,
  contentStyle,
  mobilePinnedIds,
  onMobilePinnedChange,
  mobileEditable = false,
  mobileLocked = false,
  topBar,
  topBarTitle,
  topBarStart,
  topBarActions,
  subBar,
  search,
  hideSearchOnDesktop = false,
  background = 'dots',
}: PageLayoutProps) {
  const leafItems = flattenLeaves(navItems);
  const mobileItems = leafItems.filter((item) => item.mobileNav !== false);
  const showBackground = background === 'dots';

  return (
    <SidebarProvider>
      <ShellBody
        navItems={navItems}
        activeId={activeId}
        onNavigate={onNavigate}
        header={header}
        collapsedHeader={collapsedHeader}
        secondary={secondary}
        sidebarFooter={sidebarFooter}
        mobileDrawerExtra={mobileDrawerExtra}
        mobileTitle={mobileTitle}
        mobileActions={mobileActions}
        maxWidth={maxWidth}
        padding={padding}
        contentStyle={contentStyle}
        showBackground={showBackground}
        mobileItems={mobileItems}
        mobileMaxItems={mobileMaxItems}
        mobilePinnedIds={mobilePinnedIds}
        onMobilePinnedChange={onMobilePinnedChange}
        mobileEditable={mobileEditable}
        mobileLocked={mobileLocked}
        topBar={topBar}
        topBarTitle={topBarTitle}
        topBarStart={topBarStart}
        topBarActions={topBarActions}
        subBar={subBar}
        search={search}
        hideSearchOnDesktop={hideSearchOnDesktop}
      >
        {children}
      </ShellBody>
    </SidebarProvider>
  );
}
